import { computed, toValue, type MaybeRefOrGetter, type Ref } from "vue";

import type { CheckboxFilterConfig, FilterConfig } from "@/components/episto-ui/filter-dropdown";
import { updateFilterAndSearch } from "@/utils/updateFilterAndSearch";
import { toggleArrayValue } from "@/utils/toggleArrayValue";

export interface FilterDeclaration<T = string | number | boolean> {
  id: string;
  label: string;
  hasSearch?: boolean;
  options: MaybeRefOrGetter<CheckboxFilterConfig["options"]>;
  model: Ref<T[]>;
  when?: () => boolean;
}

export const createFilters = ({
  filters,
  search,
  clear,
}: {
  filters: FilterDeclaration[];
  search?: () => unknown;
  clear?: () => void;
}) => {
  const activeFilters = computed(() => filters.filter((filter) => !filter.when || filter.when()));

  const filterConfigs = computed<FilterConfig[]>(() =>
    activeFilters.value.map((filter) => ({
      id: filter.id,
      label: filter.label,
      hasSearch: filter.hasSearch ?? false,
      options: toValue(filter.options),
      selectedValues: filter.model.value,
    })),
  );

  const activeFiltersCount = computed(() =>
    activeFilters.value.reduce((count, filter) => count + filter.model.value.length, 0),
  );

  const handleFilterChange = (filterId: string, value: string | number | boolean) => {
    const filter = filters.find(({ id }) => id === filterId);
    if (!filter) return;

    if (search) updateFilterAndSearch(filter.model, value, async () => search());
    else toggleArrayValue(filter.model, value);
  };

  const handleClear =
    clear ??
    (() => {
      filters.forEach((filter) => {
        filter.model.value = [];
      });
      search?.();
    });

  return { filterConfigs, activeFiltersCount, handleFilterChange, handleClear };
};
